import React, { useState } from "react";
import axios from "axios";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "../hooks/use-toast";
import { Home, Building2, Send, Phone, Mail, MapPin, Clock, ArrowRight } from "lucide-react";
import { company } from "../mock";
import PhoneInput from "../portal/shared/PhoneInput";
import MoneyInput from "../portal/shared/MoneyInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ResidentialForm() {
  const [f, setF] = useState({ name: "", email: "", phone: "", loanType: "", propertyType: "", amount: "", message: "" });
  const [sending, setSending] = useState(false);
  const [hp, setHp] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.phone) {
      toast({ title: "Please fill required fields", description: "Name, email and phone are required." });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/public/contact/residential`, {
        name: f.name, email: f.email, phone: f.phone,
        loan_type: f.loanType, amount: f.amount == null ? null : String(f.amount), message: f.message,
        _hp: hp,
      });
      toast({ title: "Application received!", description: "A residential loan specialist will call you within 1 business hour." });
      setF({ name: "", email: "", phone: "", loanType: "", propertyType: "", amount: "", message: "" });
    } catch (err) {
      toast({ title: "Submission failed", description: err.response?.data?.detail || "Please try again or call us." });
    }
    setSending(false);
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="text-sm" style={{ color: "#070B5F" }}>Full Name *</Label>
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5" placeholder="Jane Doe" />
        </div>
        <div>
          <Label className="text-sm" style={{ color: "#070B5F" }}>Phone *</Label>
          <PhoneInput value={f.phone} onChange={(d) => setF({ ...f, phone: d })} className="mt-1.5" />
        </div>
      </div>
      <div>
        <Label className="text-sm" style={{ color: "#070B5F" }}>Email *</Label>
        <Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-1.5" placeholder="you@example.com" />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="text-sm" style={{ color: "#070B5F" }}>Loan Type</Label>
          <Select value={f.loanType} onValueChange={(v) => setF({ ...f, loanType: v })}>
            <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select loan type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="purchase">Purchase</SelectItem>
              <SelectItem value="refinance">Refinance</SelectItem>
              <SelectItem value="fha">FHA</SelectItem>
              <SelectItem value="va">VA</SelectItem>
              <SelectItem value="usda">USDA</SelectItem>
              <SelectItem value="jumbo">Jumbo</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm" style={{ color: "#070B5F" }}>Estimated Amount</Label>
          <MoneyInput value={f.amount} onChange={(n) => setF({ ...f, amount: n })} className="mt-1.5" placeholder="$350,000" />
        </div>
      </div>
      <div>
        <Label className="text-sm" style={{ color: "#070B5F" }}>Tell us about your goals</Label>
        <Textarea value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className="mt-1.5" rows={3} placeholder="First-time buyer, refinance, timeline..." />
      </div>
      {/* honeypot: hidden from people; bots fill it and the server drops the submit */}
      <div aria-hidden="true" style={{ position: "absolute", left: "-10000px" }}>
        <input type="text" name="_hp" tabIndex={-1} autoComplete="off" value={hp}
               onChange={(e) => setHp(e.target.value)} data-testid="contact-hp" />
      </div>
      <button type="submit" disabled={sending} className="btn-gold w-full justify-center">
        {sending ? "Sending..." : <>Get My Free Consultation <ArrowRight size={16} /></>}
      </button>
      <p className="text-xs text-slate-500 text-center">Submissions route to our residential loan team via GoHighLevel.</p>
    </form>
  );
}

function CommercialForm() {
  const [f, setF] = useState({ name: "", company: "", email: "", phone: "", dealType: "", propertyType: "", amount: "", timeline: "", message: "" });
  const [sending, setSending] = useState(false);
  const [hp, setHp] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.phone) {
      toast({ title: "Please fill required fields", description: "Name, email and phone are required." });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/public/contact/commercial`, {
        name: f.name, company: f.company, email: f.email, phone: f.phone,
        deal_type: f.dealType, property_type: f.propertyType,
        amount: f.amount == null ? null : String(f.amount), timeline: f.timeline, message: f.message,
        _hp: hp,
      });
      toast({ title: "Deal inquiry received!", description: "Our commercial lending team will reach out within 1 business hour." });
      setF({ name: "", company: "", email: "", phone: "", dealType: "", propertyType: "", amount: "", timeline: "", message: "" });
    } catch (err) {
      toast({ title: "Submission failed", description: err.response?.data?.detail || "Please try again or call us." });
    }
    setSending(false);
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="text-sm" style={{ color: "#070B5F" }}>Full Name *</Label>
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5" />
        </div>
        <div>
          <Label className="text-sm" style={{ color: "#070B5F" }}>Company / Entity</Label>
          <Input value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} className="mt-1.5" />
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="text-sm" style={{ color: "#070B5F" }}>Email *</Label>
          <Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-1.5" />
        </div>
        <div>
          <Label className="text-sm" style={{ color: "#070B5F" }}>Phone *</Label>
          <PhoneInput value={f.phone} onChange={(d) => setF({ ...f, phone: d })} className="mt-1.5" />
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="text-sm" style={{ color: "#070B5F" }}>Deal Type</Label>
          <Select value={f.dealType} onValueChange={(v) => setF({ ...f, dealType: v })}>
            <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="acquisition">Acquisition</SelectItem>
              <SelectItem value="refinance">Refinance</SelectItem>
              <SelectItem value="bridge">Bridge / Hard Money</SelectItem>
              <SelectItem value="construction">Construction</SelectItem>
              <SelectItem value="loc">Business Line of Credit</SelectItem>
              <SelectItem value="working-capital">Working Capital</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm" style={{ color: "#070B5F" }}>Property Type</Label>
          <Select value={f.propertyType} onValueChange={(v) => setF({ ...f, propertyType: v })}>
            <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="multifamily">Multifamily</SelectItem>
              <SelectItem value="retail">Retail</SelectItem>
              <SelectItem value="office">Office</SelectItem>
              <SelectItem value="industrial">Industrial</SelectItem>
              <SelectItem value="mixed">Mixed-Use</SelectItem>
              <SelectItem value="sfr-investor">SFR Investor</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="text-sm" style={{ color: "#070B5F" }}>Loan Amount</Label>
          <MoneyInput value={f.amount} onChange={(n) => setF({ ...f, amount: n })} className="mt-1.5" placeholder="$1,500,000" />
        </div>
        <div>
          <Label className="text-sm" style={{ color: "#070B5F" }}>Timeline to Close</Label>
          <Input value={f.timeline} onChange={(e) => setF({ ...f, timeline: e.target.value })} className="mt-1.5" placeholder="30 / 60 / 90 days" />
        </div>
      </div>
      <div>
        <Label className="text-sm" style={{ color: "#070B5F" }}>Deal Details</Label>
        <Textarea value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className="mt-1.5" rows={3} placeholder="Location, NOI, cap rate, use of funds..." />
      </div>
      {/* honeypot: hidden from people; bots fill it and the server drops the submit */}
      <div aria-hidden="true" style={{ position: "absolute", left: "-10000px" }}>
        <input type="text" name="_hp" tabIndex={-1} autoComplete="off" value={hp}
               onChange={(e) => setHp(e.target.value)} data-testid="contact-hp" />
      </div>
      <button type="submit" disabled={sending} className="btn-navy w-full justify-center" style={{ background: "#070B5F" }}>
        {sending ? "Sending..." : <>Submit Deal Inquiry <Send size={15} /></>}
      </button>
      <p className="text-xs text-slate-500 text-center">Inquiries route to our commercial lending team via GoHighLevel.</p>
    </form>
  );
}

export default function ContactSection() {
  return (
    <section id="contact" className="section-pad relative" style={{ background: "#faf7f2" }}>
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-5 gap-10">
        {/* Info side */}
        <div className="lg:col-span-2 reveal">
          <span className="gold-bar" />
          <div className="text-xs uppercase tracking-[0.28em] font-semibold mt-3" style={{ color: "#6BA82E" }}>Contact Us</div>
          <h2 className="font-serif text-4xl md:text-5xl font-semibold mt-3 leading-tight" style={{ color: "#070B5F" }}>
            Let’s find your perfect loan.
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Choose the right form for your needs and our specialists will reach out within one business hour.
          </p>

          <div className="mt-8 space-y-5">
            <ContactRow icon={Phone} title="Call" value={company.phone} href={`tel:${company.phoneRaw}`} />
            <ContactRow icon={Mail} title="Email" value={company.email} href={`mailto:${company.email}`} />
            <ContactRow icon={MapPin} title="Office" value={`${company.address.line1}, ${company.address.city} ${company.address.state} ${company.address.zip}`} />
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(141,198,63,0.12)" }}>
                <Clock size={18} style={{ color: "#6BA82E" }} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500">Hours</div>
                {company.hours.map(h => (
                  <div key={h.day} className="text-sm mt-0.5" style={{ color: "#070B5F" }}>
                    <span className="font-medium">{h.day}</span> <span className="text-slate-500">— {h.time}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Forms side */}
        <div className="lg:col-span-3 reveal">
          <div className="bg-white rounded-3xl p-8 md:p-10 shadow-3d-lg border border-slate-100">
            <Tabs defaultValue="residential" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-8 h-auto">
                <TabsTrigger value="residential" className="py-3 flex items-center gap-2">
                  <Home size={16} /> Residential
                </TabsTrigger>
                <TabsTrigger value="commercial" className="py-3 flex items-center gap-2">
                  <Building2 size={16} /> Investment / Commercial
                </TabsTrigger>
              </TabsList>
              <TabsContent value="residential"><ResidentialForm /></TabsContent>
              <TabsContent value="commercial"><CommercialForm /></TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactRow({ icon: Icon, title, value, href }) {
  const inner = (
    <>
      <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(141,198,63,0.12)" }}>
        <Icon size={18} style={{ color: "#6BA82E" }} />
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500">{title}</div>
        <div className="text-base font-medium mt-0.5" style={{ color: "#070B5F" }}>{value}</div>
      </div>
    </>
  );
  return href ? (
    <a href={href} className="flex items-start gap-4 hover:opacity-80 transition">{inner}</a>
  ) : (
    <div className="flex items-start gap-4">{inner}</div>
  );
}
